
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronRight,
  HelpCircle,
  Medal,
  Star,
  Trophy,
  Users,
} from 'lucide-react';
import { SettingsIcon } from '@/shared/config';
import {
  apiErrorText,
  kpiApi,
  type KpiLeaderboard,
  type KpiLeaderboardRow,
  type KpiUserEvent,
} from '@/shared/api';
import { useAuthStore } from '@/features/auth';
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  PageMotion,
  Scroller,
  SearchInput,
  Skeleton,
} from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { KpiSettingsDialog } from './KpiSettingsDialog';
import { MonthSwitcher } from './MonthSwitcher';
import { currentMonth, monthLabel } from './month';
import { ScoringDialog } from './ScoringDialog';
import styles from './KpiLeaderboard.module.css';

const MEDAL_VAR = ['var(--medal-1)', 'var(--medal-2)', 'var(--medal-3)'];

const PLACE_LABEL = ['Первое место', 'Второе место', 'Третье место'];

function formatNumber(value: number): string {
  return String(Math.round(value * 100) / 100);
}

function formatPoints(points: number): string {
  return points > 0 ? `+${formatNumber(points)}` : formatNumber(points);
}

function formulaText(e: KpiUserEvent): string {
  if (e.reason !== 'ticket_closed' || e.basePoints == null) return '—';
  const parts = [
    `${e.basePoints}`,
    `× ${e.speedMultiplier ?? 1}`,
    `× ${e.complexityMultiplier ?? 1}`,
  ];
  if ((e.shareDivisor ?? 1) > 1) parts.push(`÷ ${e.shareDivisor}`);
  return parts.join(' ');
}

const Rating: React.FC<{ value: number | null; ratedCount: number }> = ({ value, ratedCount }) =>
  value == null ? (
    <span className={styles.ratingEmpty}>—</span>
  ) : (
    <span className={styles.rating} title={`Оценено заявок: ${ratedCount}`}>
      <Star size={13} className={styles.ratingStar} fill="currentColor" aria-hidden />
      {value.toFixed(2)}
    </span>
  );

const Stat: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: string;
  featured?: boolean;
}> = ({ icon, label, value, hint, featured }) => (
  <div className={cn(styles.stat, featured && styles.statFeatured)}>
    <span className={styles.statIcon} aria-hidden>
      {icon}
    </span>
    <span className={styles.statBody}>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statValue}>{value}</span>
      {hint && <span className={styles.statHint}>{hint}</span>}
    </span>
  </div>
);

const PodiumCard: React.FC<{ row: KpiLeaderboardRow; place: number }> = ({ row, place }) => (
  <div
    className={cn(
      styles.podiumCard,
      place === 0 && styles.podiumFirst,
      place === 1 && styles.podiumSecond,
      place === 2 && styles.podiumThird,
    )}
    style={{ '--medal': MEDAL_VAR[place] } as React.CSSProperties}
  >
    <Avatar className={styles.podiumAvatar} name={row.displayName} seed={row.userId} src={row.avatarUrl} />
    <div className={styles.podiumBody}>
      <span className={styles.podiumPlace}>
        <Medal size={14} aria-hidden />
        {PLACE_LABEL[place]}
      </span>
      <Link className={styles.podiumName} to={`/users/${row.userId}`}>
        {row.displayName}
      </Link>
      <span className={styles.podiumPoints}>{formatNumber(row.points)}</span>
      <span className={styles.podiumMeta}>
        <span>решено: {row.ticketsClosed}</span>
        <Rating value={row.avgRating} ratedCount={row.ratedCount} />
      </span>
    </div>
  </div>
);

const UserEvents: React.FC<{ userId: string; month: string }> = ({ userId, month }) => {
  const [events, setEvents] = useState<KpiUserEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setEvents(null);
    setError(null);
    kpiApi
      .userEvents(userId, month)
      .then((res) => alive && setEvents(res.data?.events ?? []))
      .catch((err) => alive && setError(apiErrorText(err, 'Не удалось загрузить детализацию')));
    return () => {
      alive = false;
    };
  }, [userId, month]);

  if (error) return <div className={styles.detailMessage}>{error}</div>;
  if (!events)
    return (
      <div className={styles.detailMessage}>
        <Skeleton height={14} width="60%" radius={4} />
      </div>
    );
  if (events.length === 0)
    return <div className={styles.detailMessage}>За этот месяц начислений нет</div>;

  return (
    <>
      <div className={styles.detailHead}>
        <span>Начисления · {monthLabel(month)}</span>
        <span className={styles.detailLegend}>
          расчёт: база × скорость × сложность ÷ исполнители
        </span>
      </div>
      {events.map((e, i) => (
        <div
          key={`${e.ticketId}-${i}`}
          className={cn(
            styles.eventRow,
            i % 2 === 1 && styles.eventAlt,
            e.reason === 'reversal' && styles.reversal,
          )}
        >
          <span className={styles.eventTitle}>
            { }
            <Link className={styles.nameLink} to={`/tickets/view/${e.ticketId}`}>
              {e.ticketTitle ?? e.ticketId}
            </Link>
            {e.reason === 'reversal' && (
              <span className={styles.reversalTag}>сторно (заявка переоткрыта)</span>
            )}
          </span>
          <span className={styles.eventDate}>
            {new Date(e.createdAt).toLocaleDateString('ru-RU')}
          </span>
          <span className={styles.formula}>{formulaText(e)}</span>
          <span
            className={cn(styles.eventPoints, e.points < 0 ? styles.negative : styles.positive)}
          >
            {formatPoints(e.points)}
          </span>
        </div>
      ))}
    </>
  );
};

export const KpiLeaderboardPage: React.FC = () => {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const selfId = useAuthStore((s) => s.user?.id ?? null);
  const isStaff = hasPermission('view_tickets');
  const canEditSettings = hasPermission('manage_kpi');

  const [month, setMonth] = useState(currentMonth());
  const [months, setMonths] = useState<string[]>([currentMonth()]);
  const [board, setBoard] = useState<KpiLeaderboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [scoringOpen, setScoringOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!isStaff || !month) return;
    let alive = true;
    setError(null);
    setBoard(null);
    setExpanded(null);
    kpiApi
      .leaderboard(month)
      .then((res) => alive && setBoard(res.data ?? null))
      .catch((err) => alive && setError(apiErrorText(err, 'Не удалось загрузить рейтинг')));
    return () => {
      alive = false;
    };
  }, [isStaff, month, reloadKey]);

  useEffect(() => {
    if (!isStaff) return;
    kpiApi
      .months()
      .then((res) => {
        const list = res.data ?? [];
        setMonths(list.includes(currentMonth()) ? list : [currentMonth(), ...list]);
      })
      .catch(() => setMonths([currentMonth()]));
  }, [isStaff]);

  const rows = useMemo(() => board?.rows ?? [], [board]);

  const totals = useMemo(() => {
    const points = rows.reduce((s, r) => s + r.points, 0);
    const tickets = rows.reduce((s, r) => s + r.ticketsClosed, 0);
    const rated = rows.reduce((s, r) => s + r.ratedCount, 0);
    const ratingSum = rows.reduce((s, r) => s + (r.avgRating ?? 0) * r.ratedCount, 0);
    return {
      points: Math.round(points * 100) / 100,
      tickets,
      rated,
      avgRating: rated > 0 ? ratingSum / rated : null,
    };
  }, [rows]);

  const maxPoints = rows.length > 0 ? Math.max(...rows.map((r) => r.points), 0) : 0;

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.displayName.toLowerCase().includes(q));
  }, [rows, search]);

  if (!isStaff)
    return (
      <PageMotion>
        <main className={styles.page}>
          <ErrorState title="Доступ запрещён" message="Раздел доступен инженерам поддержки" />
        </main>
      </PageMotion>
    );

  return (
    <PageMotion>
      <main className={styles.page}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>KPI инженеров</h1>
            <p className={styles.subtitle}>
              Рейтинг по очкам за решённые заявки. Очки начисляются при решении заявки и
              корректируются оценкой заявителя — раскройте строку, чтобы увидеть, из чего
              сложился результат.
            </p>
          </div>
          <div className={styles.actions}>
            <Button variant="ghost" onClick={() => setScoringOpen(true)} data-tour="kpi-rules">
              <HelpCircle size={16} aria-hidden />
              Как считаются очки
            </Button>
            {canEditSettings && (
              <Button variant="ghost" onClick={() => setSettingsOpen(true)}>
                <SettingsIcon size={16} aria-hidden />
                Настройки
              </Button>
            )}
          </div>
        </div>

        <div className={styles.toolbar}>
          <MonthSwitcher months={months} month={month} onChange={setMonth} />
          <span className={styles.toolbarDivider} aria-hidden />
          <span className={styles.muted}>
            {board ? `начислений за ${monthLabel(month).toLowerCase()}` : 'загрузка…'}
          </span>
        </div>

        {error ? (
          <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
        ) : !board ? (
          <div className={styles.tablePanel}>
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className={styles.skeletonRow}>
                <Skeleton width={30} height={30} circle />
                <Skeleton height={14} width={`${45 - i * 4}%`} radius={4} />
              </div>
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            Icon={Trophy}
            title="За этот месяц начислений нет"
            hint="Очки появляются, когда заявка переходит в решённые. Проверьте другой месяц или настройте базовые очки у категорий."
          />
        ) : (
          <>
            <div className={styles.statGrid}>
              <Stat
                featured
                icon={<Trophy size={17} />}
                label="Очков за месяц"
                value={formatNumber(totals.points)}
              />
              <Stat
                icon={<CheckCircle2 size={17} />}
                label="Решено заявок"
                value={totals.tickets}
              />
              <Stat
                icon={<Users size={17} />}
                label="Инженеров в рейтинге"
                value={rows.length}
              />
              <Stat
                icon={<Star size={17} />}
                label="Средняя оценка"
                value={totals.avgRating == null ? '—' : totals.avgRating.toFixed(2)}
                hint={
                  totals.rated > 0 ? `оценено заявок: ${totals.rated}` : 'оценённых заявок нет'
                }
              />
            </div>

            {rows.length >= 3 && (
              <div className={styles.podium}>
                {rows.slice(0, 3).map((row, i) => (
                  <PodiumCard key={row.userId} row={row} place={i} />
                ))}
              </div>
            )}

            <div className={styles.tablePanel} data-tour="kpi-table">
              <div className={styles.tableToolbar}>
                <SearchInput
                  className={styles.searchBox}
                  label="Поиск по инженеру"
                  value={search}
                  onChange={setSearch}
                />
                <span className={styles.tableCount}>
                  {search.trim()
                    ? `${visibleRows.length} из ${rows.length}`
                    : `инженеров: ${rows.length}`}
                </span>
              </div>
              {visibleRows.length === 0 ? (
              <EmptyState
                title="Никого не нашли"
                hint={`По запросу «${search.trim()}» инженеров в рейтинге нет.`}
              />
            ) : (
              <Scroller axis="x" className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.expandCell} aria-label="Детализация" />
                      <th className={styles.rankCell}>Место</th>
                      <th>Инженер</th>
                      <th className={styles.numCell}>Решено</th>
                      <th className={styles.numCell}>Оценка</th>
                      <th className={styles.pointsCell}>Очки</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row, i) => {
                      const isOpen = expanded === row.userId;
                      const isSelf = selfId != null && row.userId === selfId;
                      const share = maxPoints > 0 ? Math.max(0, row.points / maxPoints) : 0;
                      return (
                        <Fragment key={row.userId}>
                          <tr
                            className={cn(
                              styles.row,
                              i % 2 === 1 && styles.rowAlt,
                              isSelf && styles.rowSelf,
                            )}
                          >
                            <td className={styles.expandCell}>
                              <button
                                type="button"
                                className={styles.expandButton}
                                onClick={() => setExpanded(isOpen ? null : row.userId)}
                                aria-expanded={isOpen}
                                aria-label={`Начисления: ${row.displayName}`}
                              >
                                <ChevronRight
                                  size={16}
                                  aria-hidden
                                  className={cn(styles.expandIcon, isOpen && styles.expandIconOpen)}
                                />
                              </button>
                            </td>
                            <td className={styles.rankCell}>
                              {row.rank <= 3 ? (
                                <span
                                  className={styles.medal}
                                  style={
                                    { '--medal': MEDAL_VAR[row.rank - 1] } as React.CSSProperties
                                  }
                                  title={PLACE_LABEL[row.rank - 1]}
                                >
                                  <Medal size={18} aria-label={PLACE_LABEL[row.rank - 1]} />
                                </span>
                              ) : (
                                row.rank
                              )}
                            </td>
                            <td>
                              <span className={styles.engineer}>
                                <Avatar
                                  className={styles.avatar}
                                  name={row.displayName}
                                  seed={row.userId}
                                  src={row.avatarUrl}
                                />
                                <Link className={styles.nameLink} to={`/users/${row.userId}`}>
                                  {row.displayName}
                                </Link>
                                {isSelf && (
                                  <Badge variant="primary" className={styles.selfTag}>
                                    вы
                                  </Badge>
                                )}
                              </span>
                            </td>
                            <td className={styles.numCell}>{row.ticketsClosed}</td>
                            <td className={styles.numCell}>
                              <Rating value={row.avgRating} ratedCount={row.ratedCount} />
                            </td>
                            <td className={styles.pointsCell}>
                              <span className={styles.pointsValue}>{formatNumber(row.points)}</span>
                              <span className={styles.bar} aria-hidden>
                                <span
                                  className={styles.barFill}
                                  style={{ width: `${Math.round(share * 100)}%` }}
                                />
                              </span>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className={styles.detailRow}>
                              <td colSpan={6}>
                                <div className={styles.detail}>
                                  <UserEvents userId={row.userId} month={month} />
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </Scroller>
              )}
            </div>
          </>
        )}

        <ScoringDialog open={scoringOpen} onOpenChange={setScoringOpen} />
        {canEditSettings && (
          <KpiSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
        )}
      </main>
    </PageMotion>
  );
};
